// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { TrailerModelV2 } from "@store/Framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import AlertTriangleFill from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleFill";
import { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { tableProps } from "@store/Themes/common/props/table";
import TrailerTrackingPickupRequestButton from "@store/Library/Components/TrailerTrackingV2/TrailerTrackingPickupRequestButton";
import constants from "@store/constants";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export type TrailerSortField = "id" | "poNumber" | "shipmentNumber" | "orderNumbers" | "dwellTimeInDays" | "plantName";
export type TrailerSortDirection = "ascending" | "descending";

export interface TrailerTrackingV2TableProps {
    shipToAddress: string;
    shipToNumber: string;
    trailers: TrailerModelV2[];
    showRequestPickupButton?: boolean;
    sortField?: TrailerSortField;
    sortDirection?: TrailerSortDirection;
    onSortClick?: (field: TrailerSortField) => void;
}

export interface TrailerTrackingV2TableStyles {
    orderNumbersContainer: InjectableCss;
    container?: InjectableCss;
    headerText?: TypographyProps;
    noResultsContainer?: InjectableCss;
    dataTable?: DataTableProps;
    loadingOverlay?: LoadingOverlayProps;
    tableWrapper?: InjectableCss;
    headingWrapper?: FlexWrapperPresentationProps;
    trailerItemHeader?: DataTableHeaderProps;
    trailerItemCells?: DataTableCellProps;
    trailerTightHeader?: DataTableHeaderProps;
    trailerTightCells?: DataTableCellProps;
    warningIcon?: IconPresentationProps;
    dwellTimeContainer?: InjectableCss;
    modal?: ModalPresentationProps;
}

export const trailerTrackingV2TableStyles: TrailerTrackingV2TableStyles = {
    loadingOverlay: tableProps.loadingOverlay,
    tableWrapper: {
        css: css`
            position: relative;
            width: 100%;
            overflow: auto;
            max-height: ${pxToRem(372)};
        `,
    },
    headingWrapper: tableProps.headingWrapper,
    dataTable: {
        cssOverrides: {
            cell: css`
                ${x => x.theme.dataTable.defaultProps?.cssOverrides?.cell}
                white-space: wrap;
            `,
        },
    },
    warningIcon: {
        size: 16,
        css: css`
            color: ${getColor("warning")};
        `,
    },
    dwellTimeContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(10)};
        `,
    },
    noResultsContainer: {
        css: css`
            margin: ${pxToRem(30)} auto ${pxToRem(20)} auto;
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    orderNumbersContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(4)};
        `,
    },
};

const styles = trailerTrackingV2TableStyles;

const TrailerTrackingV2Table: FC<TrailerTrackingV2TableProps> = ({
    trailers,
    shipToAddress,
    shipToNumber,
    showRequestPickupButton,
    sortField = "dwellTimeInDays",
    sortDirection = "descending",
    onSortClick,
}) => {
    const history = useHistory();
    const orderHistoryPageUrl = usePageLinkByPageType(constants.pageType.orderHistory)?.url;
    const shipmentHistoryPageUrl = usePageLinkByPageType(constants.pageType.shipmentHistory)?.url;
    const orderDetailsPageUrl = usePageLinkByPageType(constants.pageType.orderDetails)?.url;
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const navigatePOHandler = (poNumber: string) => {
        history.push(`${orderHistoryPageUrl}?poNumber=${poNumber}&customerSequence=-1`);
    };

    const navigateShipmentHandler = (shipmentNumber: string) => {
        history.push(`${shipmentHistoryPageUrl}?purchaseOrderNumber=${shipmentNumber}&customerSequence=-1`);
    };

    const navigateOrderDetailsHandler = (greifOrder: string) => {
        history.push(`${orderDetailsPageUrl}?orderNumber=${greifOrder}&customerSequence=-1`);
    };

    const getSortedValue = (trailer: TrailerModelV2, field: TrailerSortField): string | number => {
        if (field === "orderNumbers") {
            return trailer.orderNumbers?.[0] ?? "";
        }
        return trailer[field] as string | number;
    };

    const sortedTrailers = [...(trailers ?? [])].sort((a, b) => {
        const aVal = getSortedValue(a, sortField);
        const bVal = getSortedValue(b, sortField);
        let cmp: number;
        if (typeof aVal === "number" && typeof bVal === "number") {
            cmp = aVal - bVal;
        } else {
            cmp = String(aVal).localeCompare(String(bVal));
        }
        return sortDirection === "descending" ? -cmp : cmp;
    });

    const getSorted = (field: TrailerSortField): "ascending" | "descending" | false => {
        return sortField === field ? sortDirection : false;
    };

    const handleSortClick = (field: TrailerSortField) => {
        if (onSortClick) {
            onSortClick(field);
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="trailerTrackingV2Table">
            <StyledWrapper {...styles.tableWrapper}>
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader
                            {...styles.trailerTightHeader}
                            title={translate("Trailer ID")}
                            sorted={getSorted("id")}
                            onSortClick={() => handleSortClick("id")}
                            data-test-selector="tableHeader_trailerID"
                        >
                            {translate("Trailer ID")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.trailerTightHeader}
                            title={translate("PO #")}
                            sorted={getSorted("poNumber")}
                            onSortClick={() => handleSortClick("poNumber")}
                            data-test-selector="tableHeader_po"
                        >
                            {translate("PO #")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.trailerItemHeader}
                            title={translate("Shipment #")}
                            sorted={getSorted("shipmentNumber")}
                            onSortClick={() => handleSortClick("shipmentNumber")}
                            data-test-selector="tableHeader_shipment"
                        >
                            {translate("Shipment #")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.trailerTightHeader}
                            title={translate("Greif Order #")}
                            sorted={getSorted("orderNumbers")}
                            onSortClick={() => handleSortClick("orderNumbers")}
                            data-test-selector="tableHeader_greifOrder"
                        >
                            {translate("Greif Order #")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.trailerTightHeader}
                            title={translate("Dwell Time")}
                            sorted={getSorted("dwellTimeInDays")}
                            onSortClick={() => handleSortClick("dwellTimeInDays")}
                            data-test-selector="tableHeader_dwelltime"
                        >
                            {translate("Dwell Time")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.trailerTightHeader}
                            title={translate("Greif Location")}
                            sorted={getSorted("plantName")}
                            onSortClick={() => handleSortClick("plantName")}
                            data-test-selector="tableHeader_greifLocation"
                        >
                            {translate("Greif Location")}
                        </DataTableHeader>
                        {showRequestPickupButton && <DataTableHeader></DataTableHeader>}
                    </DataTableHead>
                    <DataTableBody data-test-selector="trailerTrackingV2Table_tableBody">
                        {Boolean(sortedTrailers?.length) &&
                            sortedTrailers?.map(trailer => (
                                <DataTableRow key={trailer.id}>
                                    <DataTableCell
                                        {...styles.trailerTightCells}
                                        data-test-selector="trailerTrackingV2Table_tableCell_trailerID"
                                    >
                                        {trailer.id}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.trailerTightCells}
                                        data-test-selector="trailerTrackingV2Table_tableCell_po"
                                    >
                                        <Link onClick={() => navigatePOHandler(trailer.poNumber)}>
                                            {trailer.poNumber}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.trailerItemCells}
                                        data-test-selector="trailerTrackingV2Table_tableCell_shipment"
                                    >
                                        <Link onClick={() => navigateShipmentHandler(trailer.poNumber)}>
                                            {trailer.shipmentNumber}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.trailerTightCells}
                                        data-test-selector="trailerTrackingV2Table_tableCell_greifOrder"
                                    >
                                        <StyledWrapper {...styles.orderNumbersContainer}>
                                            {trailer.orderNumbers.map(orderNumber => (
                                                <Link
                                                    key={orderNumber}
                                                    onClick={() => navigateOrderDetailsHandler(orderNumber)}
                                                >
                                                    {orderNumber}
                                                </Link>
                                            ))}
                                        </StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.trailerTightCells}
                                        data-test-selector="trailerTrackingV2Table_tableCell_dwellTime"
                                    >
                                        <StyledWrapper {...styles.dwellTimeContainer}>
                                            <Typography>
                                                {trailer.dwellTimeInDays} {translate("Days").toLowerCase()}
                                            </Typography>
                                            {trailer.isDwellTimeOverComplimentaryLimit && (
                                                <Clickable onClick={() => setModalIsOpen(true)}>
                                                    <Icon {...styles.warningIcon} src={AlertTriangleFill} />
                                                </Clickable>
                                            )}
                                        </StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.trailerTightCells}
                                        data-test-selector="trailerTrackingV2Table_tableCell_greifLocation"
                                    >
                                        {trailer.plantName}
                                    </DataTableCell>
                                    {showRequestPickupButton && (
                                        <DataTableCell
                                            {...styles.trailerTightCells}
                                            data-test-selector="trailerTrackingV2Table_tableCell_requestpickup"
                                        >
                                            <TrailerTrackingPickupRequestButton
                                                shipToNumber={shipToNumber}
                                                shipToAddress={shipToAddress}
                                                trailer={trailer}
                                            />
                                        </DataTableCell>
                                    )}
                                </DataTableRow>
                            ))}
                    </DataTableBody>
                </DataTable>
                {!sortedTrailers?.length && (
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography data-test-selector="trailerTrackingV2Table_noTrailersFound">
                            {siteMessage("NoTrailersFound_Message")}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <Modal {...styles.modal} isOpen={modalIsOpen} handleClose={() => setModalIsOpen(false)}>
                <Typography variant="p">{siteMessage("DwellTimeAlert_Message")}</Typography>
            </Modal>
        </StyledWrapper>
    );
};

export default TrailerTrackingV2Table;
